import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import toast from "react-hot-toast";

interface UploadDropzoneProps {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  placeholder?: string;
}

const EXCEL_EXTENSIONS = [".xlsx", ".xlsm", ".xls"];
const EXCEL_MIME_TYPES = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroenabled.12",
];
const ACCEPT_ATTR = [
  ...EXCEL_EXTENSIONS,
  ...EXCEL_MIME_TYPES,
].join(",");

const isExcelFile = (f: File): boolean => {
  const ext = "." + (f.name.split(".").pop()?.toLowerCase() ?? "");
  if (EXCEL_EXTENSIONS.includes(ext)) return true;
  const mime = f.type.toLowerCase();
  return mime !== "" && EXCEL_MIME_TYPES.includes(mime);
};

export const UploadDropzone: React.FC<UploadDropzoneProps> = ({ file, onChange, disabled, placeholder }) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept = (f: File) => {
    if (isExcelFile(f)) {
      onChange(f);
      return;
    }
    toast.error("Seleziona un file Excel (.xlsx, .xlsm, .xls)");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <Box
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) accept(dropped);
      }}
      sx={{
        border: "2px dashed",
        borderColor: dragging ? "#1565C0" : file ? "#1565C060" : "divider",
        borderRadius: "12px",
        p: placeholder ? 2 : 4,
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        bgcolor: dragging ? "#1565C008" : file ? "#1565C005" : "#FFFFFF",
        transition: "all 0.2s",
        "&:hover": disabled ? {} : { borderColor: "#1565C080", bgcolor: "#1565C008" },
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTR}
        style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) accept(f); }}
        disabled={disabled}
      />
      {file ? (
        <>
          <InsertDriveFileIcon sx={{ fontSize: "2.5rem", color: "#1565C0", mb: 1 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{file.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {(file.size / 1024).toFixed(0)} KB — Clicca per cambiare
          </Typography>
        </>
      ) : (
        <>
          <UploadFileIcon sx={{ fontSize: "2.5rem", color: "text.disabled", mb: 1 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {placeholder ?? "Trascina qui il file Excel di gara"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            oppure clicca per sfogliare — xlsx, xlsm, xls (max 5 MB)
          </Typography>
        </>
      )}
    </Box>
  );
};
